import {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  useToast,
  ToastType,
} from "../../../../../context_providers/toast/toast_ctx";

import ItemSearchFieldV2 from "../../../../purchase_order/purchase_order_create/components/item_search_widget";
import {
  inwardDefaultValueV2,
  InwardModel,
  itemMovementDefaultValueV2,
  ItemMovementModelV2,
} from "../../../../inventory/models/inventory_voucher";
import Icon from "@mdi/react";
import { mdiCheck, mdiClose, mdiDelete, mdiPencil, mdiPlus } from "@mdi/js";
import ModalDialog from "../../../../../ui/new_components/common/modal";
import { UniqueProductModel } from "../../../../inventory/models/stock_transfer_model";
import { debounce } from "lodash";
import Papa from "papaparse";
import { ScannedBarCodeScanForDeviceGRNAPI } from "../service/inward_repo";
import { useModal } from "../../../../../context_providers/modal/modal_context";

interface ItemRowProps {
  max_qty?: number;

  index: number;
  item?: InwardModel;
  onSubmit: (data: ItemMovementModelV2) => void;
  edit: boolean;
  disabled: boolean;
  onDelete?: (i: ItemMovementModelV2) => void;
  projectDetails?: any;
}

const ItemRowV2: FunctionComponent<ItemRowProps> = (props) => {
  const [existingItem, setExistingItem] = useState<InwardModel>({
    ...inwardDefaultValueV2,
  });
  const [newItem, setNewItem] = useState<ItemMovementModelV2>({
    ...itemMovementDefaultValueV2,
  });
  const [choiceModal, setChoiceModal] = useState<boolean>(false);
  const [scan, setScan] = useState<boolean>(false);
  const [scannedID, setScannedID] = useState<string>("");
  const [scannedDevices, setScannedDevices] = useState<UniqueProductModel[]>(
    [],
  );
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string | null>(null);
  const { loading, setLoading } = useModal();
  const { showToast } = useToast();
  const { edit, onSubmit } = props;
  const [addProduct, setAddProduct] = useState(false);
  const [editable, setEditable] = useState(false);
  const scannedInputFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scan && scannedInputFieldRef.current) {
      scannedInputFieldRef.current.focus();
    }
  }, [scan]);

  const getDeviceIDs = async (scanned_id: string) => {
    try {
      if (scanned_id && scanned_id.length !== 6 && scanned_id.length !== 8) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid scanned ID",
        });
        return false;
      }
      setLoading(true);
      const res = await ScannedBarCodeScanForDeviceGRNAPI(
        props?.item?.item?.item_code!,
        scanned_id,
      );

      if (res.success) {
        if (res.duplicate_devices.length > 0) {
          const errorMessages = Object.entries(res.duplicate_devices)
            .map(([key, value]) =>
              typeof value === "object"
                ? `${key}: ${JSON.stringify(value)}`
                : `${key}: ${value}`,
            )
            .join("\n");
          setErrorMessages(errorMessages);
          setShowErrorModal(true);
        }
        setScannedDevices((prev) => {
          const existing = new Set(prev.map((d) => d.unique_id));
          const newOnes = res.devices.filter(
            (d: any) => !existing.has(d.unique_id),
          );
          if (newOnes.length === 0) {
            showToast({
              type: ToastType.warning,
              text: "All scanned devices are already added.",
            });
            return prev;
          }
          showToast({
            type: ToastType.success,
            text: `Added ${newOnes.length} new device(s).`,
          });
          return [...prev, ...newOnes];
        });
        setScannedID("");
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
      setLoading(false);
      return;
    } catch (error: any) {
      setLoading(false);
      showToast({
        type: ToastType.error,
        text: error.message,
      });
    }
  };

  const onScannedDeviceCallback = useCallback(
    debounce(({ scanned_id }: { scanned_id: string }) => {
      getDeviceIDs(scanned_id);
    }, 500),
    [],
  );

  useEffect(() => {
    if (scannedID.length === 8) {
      onScannedDeviceCallback({ scanned_id: scannedID });
    }
  }, [scannedID, onScannedDeviceCallback]);

  const handleDownloadTemplate = () => {
    const headers = ["Item Code", "Unique ID", "Item Name"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "item_inward_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const rawData = results.data as Record<string, string>[];

        if (rawData.length === 0) {
          alert("CSV file is empty or invalid.");
          return;
        }

        // Trim all keys and values
        const parsedData = rawData.map((row) => {
          const trimmedRow: Record<string, string> = {};
          for (const key in row) {
            const trimmedKey = key.trim();
            const trimmedValue = row[key]?.trim() ?? "";
            trimmedRow[trimmedKey] = trimmedValue;
          }
          return trimmedRow;
        });

        const firstRow = parsedData[0];
        const requiredHeaders = ["Item Code", "Unique ID", "Item Name"];
        const missingHeaders = requiredHeaders.filter(
          (key) => !(key in firstRow),
        );

        if (missingHeaders.length > 0) {
          alert(`Missing column(s) in CSV: ${missingHeaders.join(", ")}`);
          return;
        }

        const uniqueIdList = parsedData.map((row) => row["Unique ID"]);

        // Check for duplicate Unique IDs
        const idFrequency: Record<string, number> = {};
        const duplicates: string[] = [];

        for (const id of uniqueIdList) {
          if (!id) continue;
          idFrequency[id] = (idFrequency[id] || 0) + 1;
          if (idFrequency[id] === 2) {
            duplicates.push(id);
          }
        }

        if (duplicates.length > 0) {
          alert(`Duplicate Unique ID(s) found: ${duplicates.join(", ")}`);
          return;
        }

        const formattedData = parsedData.map((row) => ({
          unique_id: row["Unique ID"],
          item_name: row["Item Name"],
          item_code: row["Item Code"],
        }));

        // Check for mismatched item codes
        const currentItemCode = props.item?.item?.item_code?.trim();
        const mismatchedItems = formattedData.filter(
          (data) => data.item_code !== currentItemCode,
        );

        if (mismatchedItems.length > 0) {
          const mismatchedCodes = [
            ...new Set(mismatchedItems.map((d) => d.item_code)),
          ]
            .filter(Boolean)
            .join(", ");
          showToast({
            type: ToastType.error,
            text: `Device ID(s) ${mismatchedCodes} cannot be Inwarded on Device ID ${
              currentItemCode ?? "--"
            }`,
          });
          return;
        }

        setNewItem({
          ...newItem,
          uniqueIds: formattedData,
        });

        setExistingItem({
          ...existingItem,
          uniqueIds: formattedData,
        });
      },
    });
  };

  useEffect(() => {
    setEditable(edit);
  }, [edit]);

  useEffect(() => {
    setExistingItem({ ...(props.item || inwardDefaultValueV2) });
    // New item will be the same as existing item but with changed recieved unit number
    // and a changed Voucher Id and Voucher number
    setNewItem({ ...(props.item || itemMovementDefaultValueV2) });
  }, [props.item]);

  const handleInputChange = (index: number, value: string) => {
    const updatedUniqueIds: UniqueProductModel[] = Array.isArray(
      existingItem.uniqueIds,
    )
      ? [...existingItem.uniqueIds]
      : [];
    updatedUniqueIds[index].unique_id = value;
    setNewItem({ ...newItem, uniqueIds: updatedUniqueIds });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger file input
    }
  };

  const handleAdd = () => {
    const newRow: UniqueProductModel = {
      unique_id: "",
      item_name: existingItem.item?.name || "",
    };

    setNewItem({
      ...newItem,
      uniqueIds: [...(newItem.uniqueIds || []), newRow],
    });

    setExistingItem({
      ...existingItem,
      uniqueIds: [...(existingItem.uniqueIds || []), newRow],
    });
  };

  // Delete row
  const handleDelete = (index: number) => {
    const updatedUniqueIds = [...(existingItem.uniqueIds || [])];
    updatedUniqueIds.splice(index, 1);
    setExistingItem({ ...existingItem, uniqueIds: updatedUniqueIds });
    setNewItem({ ...existingItem, uniqueIds: updatedUniqueIds });
  };

  const handleDeleteDevice = (device_id: string) => {
    setScannedDevices((prev) => prev.filter((d) => d.unique_id !== device_id));
  };

  const submitDeviceIds = () => {
    setNewItem((prev) => ({
      ...prev,
      uniqueIds: scannedDevices,
      received_unit_no: scannedDevices.length,
    }));
    setExistingItem({
      ...existingItem,
      uniqueIds: scannedDevices,
      received_unit_no: scannedDevices.length,
    });
    setScan(false);
    setChoiceModal(false);
  };

  const submitUploadCSVDeviceIds = () => {
    setAddProduct(false);
    setChoiceModal(false);
  };

  const onDone = () => {
    if (
      !existingItem.item?.name ||
      newItem?.received_unit_no === undefined ||
      newItem?.received_unit_no === null
    ) {
      showToast({
        type: ToastType.warning,
        text: "Please select Item, received Qty and price",
      });
      return;
    }

    if (
      newItem.item?.subcategory !== "Finished Goods" &&
      newItem.item?.subcategory !== "IT and Electronic Hardware"
    ) {
      delete newItem.uniqueIds;
    } else if (newItem.uniqueIds) {
      //   newItem.uniqueIds.forEach((object) => {
      //     if (object.unique_id === "" || object.unique_id === undefined) {
      //       showToast({
      //         type: ToastType.error,
      //         text: "Please enter a valid Item ID",
      //       });
      //       return;
      //     }
      //   })
      const uniqueIdsList = newItem.uniqueIds.map((obj) => obj.unique_id);

      // Check Empty Unique ID
      const hasEmptyId = uniqueIdsList.some((id) => !id || id.trim() === "");
      if (hasEmptyId) {
        showToast({
          type: ToastType.error,
          text: "Please enter a valid Item ID",
        });
        return;
      }

      // Check Duplicate Unique IDs
      const duplicates = uniqueIdsList.filter(
        (id, index) => uniqueIdsList.indexOf(id) !== index,
      );

      if (duplicates.length > 0) {
        showToast({
          type: ToastType.error,
          text: `Duplicate Unique Id(s) found: ${[...new Set(duplicates)].join(
            ", ",
          )}`,
        });
        return;
      }
    }

    // because we need to create a new Voucher
    newItem.voucher_id = "";

    if (
      newItem.received_unit_no + existingItem?.total_received_qty! >
      existingItem?.total_ordered_qty!
    ) {
      showToast({
        text: "Recieved Items are exceeding Total Ordered Quantity",
        type: ToastType.error,
      });
      return;
    }

    onSubmit(newItem);
  };

  const buttonClass =
    "border rounded-lg px-4 py-2 hover:bg-gray-800 hover:text-white";

  return (
    <>
      {showErrorModal && (
        <ModalDialog
          show={showErrorModal}
          title="Import Errors"
          onClose={() => setShowErrorModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              {errorMessages}
            </pre>
          </div>
        </ModalDialog>
      )}
      <div className="p-1">
        {choiceModal && (
          <ModalDialog
            show={choiceModal}
            title={
              scan
                ? "Scan Barcode"
                : addProduct
                  ? "Upload CSV"
                  : "Choose Upload Method"
            }
            onClose={() => {
              setChoiceModal(false);
              setScan(false);
              setAddProduct(false);
            }}
          >
            {/* Step 1: Show scan section if selected */}
            {scan ? (
              <section>
                <div className="bg-gray-50 p-3 m-2 border rounded-lg">
                  <input
                    type="text"
                    value={scannedID}
                    placeholder="Scan Barcode"
                    className="font-semibold h-full"
                    ref={scannedInputFieldRef}
                    disabled={loading}
                    onChange={(e) => setScannedID(e.target.value)}
                  />
                </div>
                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm text-left table-auto border-collapse">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-2">Device ID</th>
                        <th className="px-4 py-2">Item Code</th>
                        {/* <th className="px-4 py-2">Manufacturer ID</th> */}
                        <th className="px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scannedDevices.map((device) => (
                        <tr
                          key={device.unique_id}
                          className={`border-b hover:bg-gray-50 ${
                            device.inventory_found === false
                              ? "bg-gray-200"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-2">{device.unique_id}</td>
                          <td className="px-4 py-2">{device.item_code}</td>
                          {/* <td className="px-4 py-2">
                            {device.manufacturer_id}
                          </td> */}
                          <td className="px-4 py-2">
                            <button
                              onClick={() =>
                                handleDeleteDevice(device.unique_id)
                              }
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {scannedDevices.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center py-3 text-gray-500"
                          >
                            No scanned devices.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2 my-4">
                  <button
                    className={buttonClass}
                    onClick={() => {
                      setChoiceModal(true);
                      setScan(false);
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className={buttonClass}
                    onClick={() => {
                      submitDeviceIds();
                    }}
                  >
                    {props.item ? "Update" : "Create"}
                  </button>
                </div>
              </section>
            ) : addProduct ? (
              // Step 2: Show CSV upload section if selected
              <div className="p-4">
                <div className="flex items-center gap-4 mb-4">
                  {/* <label
                    onClick={handleDownloadTemplate}
                    className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
                  >
                    Template
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv"
                    onChange={handleCSVUpload}
                    className="hidden"
                  /> */}
                  {/* <button
                    onClick={handleButtonClick}
                    title="Required fields - Item Code, Unique ID, Item Name"
                    className="bg-green-600 hover:bg-green-700 transition-colors text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Upload CSV
                  </button> */}
                </div>
                <div className="overflow-x-auto max-h-60 border border-gray-300 rounded">
                  <table className="w-full border border-gray-300">
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="p-2 border">Unique Ids</th>
                        <th className="p-2 border">Item Name</th>
                        <th className="p-2 border">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(newItem.uniqueIds || []).map((row: any, index) => (
                        <tr key={row.id} className="text-center">
                          <td className="p-2 border">
                            <input
                              type="text"
                              value={row.unique_id || ""}
                              onChange={(e) =>
                                handleInputChange(index, e.target.value)
                              }
                              className="p-1 border rounded w-full focus:outline-none"
                              placeholder="Enter Unique Id"
                            />
                          </td>
                          <td className="p-2 border">{row.item_name}</td>
                          <td className="p-2 border">
                            <div
                              onClick={() => handleDelete(index)}
                              className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200 cursor-pointer inline-block"
                            >
                              <Icon path={mdiDelete} className="h-4 w-4" />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {/* <tr>
                        <td colSpan={3} className="p-2 border text-center">
                          <div
                            onClick={handleAdd}
                            className="bg-blue-100 p-1 rounded text-blue-800 hover:scale-110 transform duration-200 cursor-pointer inline-flex items-center space-x-1"
                          >
                            <Icon path={mdiPlus} className="h-4 w-4" />
                            <span>Add Row</span>
                          </div>
                        </td>
                      </tr> */}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2 my-4">
                  <button
                    className={buttonClass}
                    onClick={() => {
                      setChoiceModal(true);
                      setAddProduct(false);
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className={buttonClass}
                    onClick={() => {
                      submitUploadCSVDeviceIds();
                    }}
                  >
                    {props.item ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            ) : (
              // Step 3: Show initial method choice
              <div className="flex justify-center gap-6 py-4">
                <button
                  type="button"
                  className={buttonClass}
                  onClick={() => {
                    setScan(true);
                    setAddProduct(false);
                  }}
                >
                  Scan ID
                </button>
                <button
                  type="button"
                  title="Required fields - Item Code, Unique ID, Item Name"
                  className={buttonClass}
                  onClick={() => {
                    setAddProduct(true);
                    setScan(false);
                  }}
                >
                  Upload CSV
                </button>
              </div>
            )}
          </ModalDialog>
        )}

        {/* {scan && (
          <section>
            <div className="bg-gray-50 p-3 m-2 border rounded-lg">
              <input
                type="text"
                value={scannedID}
                placeholder="Scan Barcode"
                className="font-semibold h-full"
                ref={scannedInputFieldRef}
                disabled={loading ? true : false}
                onChange={(e) => setScannedID(e.target.value)}
              />
            </div>
            <div className="overflow-auto border rounded-lg">
              <table className="w-full text-sm text-left table-auto border-collapse">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-4 py-2">Device ID</th>
                    <th className="px-4 py-2">Device Group</th>
                    <th className="px-4 py-2">Manufacturer ID</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scannedDevices.map((device) => (
                    <>
                      <tr
                        key={device.unique_id}
                        className={`border-b hover:bg-gray-50 ${
                          device.inventory_found === false ? "bg-gray-200" : ""
                        }`}
                      >
                        <td className="px-4 py-2">{device.unique_id}</td>
                        <td className="px-4 py-2">{device.device_group}</td>
                        <td className="px-4 py-2">{device.manufacturer_id}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleDeleteDevice(device.unique_id)}
                            className="text-red-600 hover:text-red-800 font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    </>
                  ))}

                  {scannedDevices.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-3 text-gray-500"
                      >
                        No scanned devices.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )} */}
        <div className="grid grid-cols-12 gap-x-2 text-sm px-2 items-center border-b py-1">
          {/* Index */}
          {props.index}
          {/* Item Code Input */}
          <input
            disabled={true}
            value={existingItem?.item?.item_code || ""}
            type="text"
            className="text-center p-1 focus:outline-none border rounded w-full"
          />
          {/* Item Name */}{" "}
          <ItemSearchFieldV2
            disabled={true}
            value={existingItem?.item}
            onSelect={(d) => {
              //   if (d.type !== ItemType.service) setSelectedItem(d);
              //   else {
              //     setData((o) => ({ ...o, item: d, item_id: d.id }));
              //   }
            }}
          />
          {/* Unit measure */}
          <input
            disabled={true}
            value={existingItem?.item?.unit?.symbol || ""}
            type="text"
            className="text-right p-1 focus:outline-none border rounded w-full"
          />
          {/* Total PO Quantity */}
          <input
            disabled={true}
            value={existingItem?.total_ordered_qty || ""}
            type="text"
            className="text-right p-1 focus:outline-none border rounded w-full"
          />
          {/* Previously recieved Quantity */}
          <input
            disabled={true}
            value={existingItem?.total_received_qty || 0}
            type="text"
            className="text-right p-1 focus:outline-none border rounded w-full"
          />
          {/* Recieved Quantity */}
          <input
            disabled={editable === false ? true : false}
            type="number"
            className="text-right p-1 focus:outline-none border rounded w-full"
            value={newItem?.received_unit_no || ""}
            onChange={(e) => {
              const val = e.target.value; // Trim whitespace
              setNewItem((prevData) => ({
                ...prevData,
                received_unit_no: Number(val), // Convert input to a number
              }));
            }}
            // onChange={(e) => {
            //   setNewItem({
            //     ...newItem,
            //     received_unit_no: parseNumber(e.target.value) ?? 0,
            //   });
            // }}
          />
          <div className="text-center p-1 w-full">
            {props.projectDetails?.project?.name}
          </div>
          <div className="text-center p-1 w-full">
            {props.projectDetails?.project?.cost_center_category}
          </div>
          <div className="text-center p-1 w-full">
            {props.projectDetails?.project?.cost_center_id}
          </div>
          {/* Unique ID  */}
          <div>
            {existingItem.item?.type === "Product" &&
            (existingItem.item?.subcategory === "Finished Goods" ||
              existingItem.item?.subcategory ===
                "IT and Electronic Hardware") ? (
              <div
                onClick={() => {
                  setChoiceModal(true);
                }}
                className="text-left p-1 focus:outline-none border rounded w-full cursor-pointer text-blue-800"
              >
                <div className="flex items-center space-x-2">
                  <Icon path={mdiPlus} className="h-6 w-4" />
                  <span>
                    {(existingItem.uniqueIds?.length ?? 0) > 0
                      ? `${existingItem.uniqueIds?.length ?? 0} items added`
                      : "Upload/Scan"}
                  </span>
                </div>
              </div>
            ) : (
              <input
                className="text-left p-1 focus:outline-none border rounded w-full"
                placeholder="unique"
                // value={existingItem.uniqueIds?.length + "items added"}
                value={""}
                onClick={() => {}}
              />
            )}
          </div>
          {props.item ? (
            !props.disabled && (
              <>
                <div className="flex justify-center gap-1 items-end">
                  {editable ? (
                    <>
                      <div
                        onClick={() => {
                          if (!window.confirm("Are you sure !")) return;
                          onDone();
                          setEditable(false);
                        }}
                        className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                      >
                        <Icon path={mdiCheck} className="h-4 w-4"></Icon>
                      </div>
                      <div
                        onClick={() => {
                          setNewItem(
                            props.item!,
                            //   {
                            //   // ...newItem,
                            //   // uniqueIds: formattedData,
                            // }
                          );

                          // setExistingItem({
                          //   ...existingItem,
                          //   uniqueIds: formattedData,
                          // });
                          setExistingItem(props.item!);
                          setEditable(false);
                        }}
                        className="bg-red-100 p-1 rounded text-red-800 hover:scale-110 transform duration-200"
                      >
                        <Icon path={mdiClose} className="h-4 w-4"></Icon>
                      </div>
                    </>
                  ) : (
                    <div
                      onClick={() => setEditable(true)}
                      className="bg-green-100 p-1 rounded text-green-800 hover:scale-110 transform duration-200"
                    >
                      <Icon path={mdiPencil} className="h-4 w-4"></Icon>
                    </div>
                  )}
                </div>
              </>
            )
          ) : (
            <button
              onClick={onDone}
              className="flex gap-1 text-white text-sm bg-myPrimaryColor rounded-full px-2 py-1  "
            >
              <Icon path={mdiPlus} className="h-5 w-5 "></Icon> Add
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ItemRowV2;
